import { Factory, Model, type ModelInstance } from 'miragejs'
import {
  getQueryParamValue,
  generateFinanceAmount,
} from '@/api/mock-server/utils.js'
import { randFloat } from '@ngneat/falso'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@/api/mock-server/server'
import type { SchemaAttributes } from '@/api/types'
import {
  ClientStatementDateFilter,
  ClientStatementDateFieldKeyMap,
  type ClientStatementDateFilters,
} from '@/models/clientStatement.js'
import type { InvoiceRowData } from '@/composables/api/Invoice.js'
import { formatToDateTimeISO } from '@/utils/date/conversion.js'
import { getEndOfPeriod } from '@/utils/date/getter.js'
import { isAfter, isBefore, isValidDate } from '@/utils/date/validation.js'
import { roundEven, toNumber } from '@/utils/number.js'

export type ClientStatementTotalBalanceData =
  SchemaAttributes<'ClientStatementTotalBalance'>

export const model = Model as ModelDefinition<ClientStatementTotalBalanceData>

const getFinanceAmount = (min: number, max: number, dec: number) =>
  roundEven(Number(generateFinanceAmount({ min, max, dec })), 2)

export const factory = Factory.extend<ClientStatementTotalBalanceData>({
  invoice_balance_at_end_date() {
    return getFinanceAmount(200, 500, 0.01)
  },
  prepayment_balance_at_end_date() {
    return roundEven(
      randFloat({
        min: 0,
        max: toNumber(this.invoice_balance_at_end_date),
        precision: 0.01,
      }),
      2,
    )
  },
  balance_at_end_date() {
    return (
      toNumber(this.invoice_balance_at_end_date) -
      toNumber(this.prepayment_balance_at_end_date)
    )
  },
  opening_balance() {
    return getFinanceAmount(-500, 1000, 0.01)
  },
  closing_balance() {
    return (
      toNumber(this.opening_balance) +
      toNumber(this.invoice_balance_at_end_date) -
      toNumber(this.prepayment_balance_at_end_date)
    )
  },
})

export function routes(server: MockServer) {
  server.get(
    '/client_statements/total_balance',
    function (this: MirageServer, schema, request) {
      const clientIdParam = getQueryParamValue(
        request?.queryParams?.['filter[client]'],
      )
      const clientStatementIdParam = getQueryParamValue(
        request?.queryParams?.['filter[client_statement_id]'],
      )
      const startDateParam = getQueryParamValue(
        request?.queryParams?.['filter[start_date]'],
      )
      const endDateParam = getQueryParamValue(
        request?.queryParams?.['filter[end_date]'],
      )
      const startDate =
        startDateParam && typeof startDateParam === 'string'
          ? formatToDateTimeISO(startDateParam)
          : null
      const endDate =
        endDateParam && typeof endDateParam === 'string'
          ? getEndOfPeriod(endDateParam, 'day')
          : null

      // Get all Invoices that belong to the Client Statement
      const invoices = schema.where('invoice', {
        clientId: clientIdParam,
        ...(clientStatementIdParam
          ? { clientStatementId: clientStatementIdParam }
          : {}),
      })

      // Filter
      let filteredInvoices = invoices

      // Date filtering.
      // Filters Invoices based on the "Date Filter" param.
      const dateFilterParam =
        getQueryParamValue(request?.queryParams?.['filter[date_filter]']) ||
        ClientStatementDateFilter.InvoiceDueDate
      const dateField =
        ClientStatementDateFieldKeyMap[
          dateFilterParam as ClientStatementDateFilters
        ]

      filteredInvoices = filteredInvoices.filter((invoice) => {
        const invoiceDate =
          formatToDateTimeISO(invoice.attrs[dateField] || '') || ''
        if (!isValidDate(invoiceDate)) {
          return false
        }

        const startComparison = !startDate || !isBefore(invoiceDate, startDate)
        const endComparison = !endDate || !isAfter(invoiceDate, endDate)

        return startComparison && endComparison
      })

      // Patients filtering.
      // Filters out Invoices that don't have any of the patients in their
      // respective Invoice Rows.
      const patientsFilterParam = getQueryParamValue(
        request?.queryParams['filter[patients]'],
      )

      const patientsFilterArray = patientsFilterParam?.split(',')
      if (patientsFilterArray?.length) {
        filteredInvoices = filteredInvoices.filter((invoice) => {
          const invoiceRowIds = invoice.attrs.rowIds
          const invoiceRows = invoiceRowIds?.map((id: string) =>
            schema.find('invoiceRow', id),
          ) as ModelInstance<Partial<InvoiceRowData> & { patientId?: string }>[]

          return invoiceRows.some((row) =>
            patientsFilterArray.includes(row.attrs.patientId?.toString() ?? ''),
          )
        })
      }

      // Get all Prepayments that belong to the Client Statement
      const prepayments = schema.where('prepayment', {
        clientId: clientIdParam,
      })

      // Check for current_balance setting
      const additionalFilters = getQueryParamValue(
        request?.queryParams?.['filter[additional_filters]'],
      )
      const isCurrentBalanceEnabled =
        JSON.parse(additionalFilters || '{}')?.current_balance === true

      let filteredPrepayments
      if (isCurrentBalanceEnabled) {
        // If current_balance is enabled, include ALL prepayments for this client
        // This ensures amounts include payments/prepayments created after end_date
        filteredPrepayments = prepayments
      } else {
        // Normal date filtering for prepayments
        filteredPrepayments = prepayments.filter(({ attrs }) => {
          const date = formatToDateTimeISO(attrs.date_added || '') || ''
          return (
            isValidDate(date) &&
            (!startDate || !isBefore(date, startDate)) &&
            (!endDate || !isAfter(date, endDate))
          )
        })

        // Filter payments for each invoice based on date range
        filteredInvoices.models.forEach((invoice) => {
          // Get all payment IDs for this invoice
          const paymentIds = invoice.attrs.paymentIds || []
          let filteredPaymentsSum = 0

          // Filter payment IDs to only include those within the date range
          const filteredPaymentIds = paymentIds.filter((paymentId: string) => {
            const payment = schema.find('invoicePayment', paymentId)
            if (!payment) {
              return false
            }

            const paymentDate =
              formatToDateTimeISO(payment.attrs?.date_added || '') || ''
            if (!isValidDate(paymentDate)) {
              return false
            }

            const isWithinRange =
              (!startDate || !isBefore(paymentDate, startDate)) &&
              (!endDate || !isAfter(paymentDate, endDate))

            if (!isWithinRange) {
              filteredPaymentsSum += toNumber(payment.attrs.paid)
            }

            return isWithinRange
          })

          // Modify the payments attritubes being returned in the query,
          // without actually modifying their values in the model's database
          invoice.attrs.paymentIds = filteredPaymentIds
          invoice.attrs.total_paid =
            invoice.attrs.total_paid - filteredPaymentsSum
          invoice.attrs.due_sum =
            invoice.attrs.total_with_vat - invoice.attrs.total_paid
          invoice.attrs.due_sum_at_end_date = invoice.attrs.due_sum
          invoice.attrs.client_due_sum = invoice.attrs.due_sum
        })
      }

      const invoiceBalanceAtEndDate = (filteredInvoices.models || []).reduce(
        (sum, { attrs }) => sum + toNumber(attrs.due_sum_at_end_date),
        0,
      )

      const prepaymentBalanceAtEndDate = (
        filteredPrepayments.models || []
      ).reduce(
        (sum, { attrs }) => sum + toNumber(attrs.unused_amount_at_end_date),
        0,
      )

      const openingBalance = getFinanceAmount(-500, 1000, 0.01)
      const closingBalance =
        openingBalance + invoiceBalanceAtEndDate - prepaymentBalanceAtEndDate

      return {
        data: {
          id: '0',
          type: 'ClientStatementTotalBalance',
          attributes: {
            invoice_balance_at_end_date: invoiceBalanceAtEndDate,
            prepayment_balance_at_end_date: prepaymentBalanceAtEndDate,
            balance_at_end_date:
              invoiceBalanceAtEndDate - prepaymentBalanceAtEndDate,
            opening_balance: openingBalance,
            closing_balance: closingBalance,
          },
        },
      }
    },
  )
}
